import { Box, SelectChangeEvent } from '@mui/material';
import DateRangeBaseField from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.component';
import { getDateRangeName } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import SelectBase from 'components/forms/fields/select-field/select-base/select-base.component';
import { DateRangeFilterComponentProps } from 'components/search/search.context';
import { useSearchParam } from 'components/search/search.hooks';
import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusFilter } from '../search-filters.hooks';
import { useTimeZoneFilter } from './date-filter.hooks';
import { getPreviousPeriod, onDateChange } from './date-filter.utils';

type DateFilterProps = {
  startParamName: string;
  endParamName: string;
  compareStartParamName: string;
  compareEndParamName: string;
  label?: string;
};

const ComparisonDateRangeFilter = ({
  startParamName,
  endParamName,
  compareStartParamName,
  compareEndParamName,
  label,
}: DateFilterProps) => {
  const { t } = useTranslation();
  const {
    value: startdate,
    setValue: setStartdate,
    setFocus,
    searchParam: {
      filter: { componentProps },
    },
  } = useSearchParam<DateTime>(startParamName);
  const {
    selectBase: selectBaseProps = {},
    dateRangeBaseField: dateRangeBaseFieldProps = {},
  } = (componentProps as DateRangeFilterComponentProps) ?? {};
  const { value: enddate, setValue: setEnddate } =
    useSearchParam<DateTime>(endParamName);
  const { value: compareStartDate } = useSearchParam<DateTime>(
    compareStartParamName,
  );
  const { value: compareEndDate } =
    useSearchParam<DateTime>(compareEndParamName);
  const initialDateRanges = {
    'previous-period': getPreviousPeriod(compareStartDate, compareEndDate),
    custom: getPreviousPeriod(compareStartDate, compareEndDate),
  };
  const options = [
    {
      id: 'previous-period',
      label: t('forms.fields.dateRange.options.previousPeriod'),
    },
    {
      id: 'custom',
      label: t('forms.fields.dateRange.options.customDateRange'),
    },
  ];
  const previousStartDateRef = useRef(compareStartDate);
  const previousEndDateRef = useRef(compareEndDate);
  const { ref, open, setOpen } = useFocusFilter(setFocus);

  const onChange = onDateChange(setStartdate, setEnddate);
  useTimeZoneFilter(startParamName, endParamName);

  const onSelectChange = (event: SelectChangeEvent) => {
    if (event.target.value === 'custom') {
      setOpen(true);
    } else {
      const dates = getPreviousPeriod(compareStartDate, compareEndDate);
      setStartdate(dates[0]);
      setEnddate(dates[1]);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        !compareStartDate.equals(previousStartDateRef.current) ||
        !compareEndDate.equals(previousEndDateRef.current)
      ) {
        previousStartDateRef.current = compareStartDate;
        previousEndDateRef.current = compareEndDate;
        const dates = getPreviousPeriod(compareStartDate, compareEndDate);
        setStartdate(dates[0]);
        setEnddate(dates[1]);
      }
    }, 150);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareEndDate, compareStartDate]);

  return (
    <Box display="flex">
      <Box display="flex" flex={1}>
        <SelectBase
          value={getDateRangeName([startdate, enddate], initialDateRanges)}
          options={options}
          onChange={onSelectChange}
          data-testid="DateFilter.Predefined"
          sx={{
            '& fieldset': {
              borderRadius: '4px 0 0 4px',
            },
          }}
          {...selectBaseProps}
        />
      </Box>
      <Box display="flex" flex={1}>
        <DateRangeBaseField
          value={[startdate, enddate]}
          sx={{
            '& fieldset': {
              borderRadius: '0 4px 4px 0',
            },
          }}
          onChange={onChange}
          dateRangePickerRef={ref}
          open={open && !dateRangeBaseFieldProps.disabled}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          disableFuture
          label={label}
          setOpen={setOpen}
          maxDate={compareStartDate.minus({ days: 1 })}
          {...dateRangeBaseFieldProps}
        />
      </Box>
    </Box>
  );
};

export default ComparisonDateRangeFilter;
